import React                     from 'react';
import View                      from '@widesk-ui/components/View';
import ImageThumbnail            from '@/TokamapEditor/ux/AreaSidebar/ImageThumbnail';
import InputOk                   from '@/TokamapEditor/ux/AreaSidebar/InputOk';
import pickMapImage              from '@/TokamapEditor/helpers/pickMapImage';
import validateImageUrl          from '@/TokamapEditor/helpers/validateImageUrl';
import { observer }              from 'mobx-react-lite';
import { ImposedField }          from '@/TokamapEditor/ux/ThemeFields';
import { ThemeSlider }           from '@/TokamapEditor/ux/ThemeFields';
import { ThemeShareSlider }      from '@/TokamapEditor/ux/ThemeFields';
import { ThemePositionField }    from '@/TokamapEditor/ux/ThemeFields';
import { ThemeOrientationField } from '@/TokamapEditor/ux/ThemeFields';
import themeStore                from '@/TokamapEditor/stores/themeStore';
import { Theme }                 from '@/TokamapEditor/stores/themeStore';
import { ThemeImageStyle }       from '@/TokamapEditor/stores/themeStore';
import { MIN_IMAGE_SCREEN_PX }   from '@/TokamapEditor/ux/constants';

const SLIDER_MAX_MIN_SCREEN_PX = 200;
const MAX_MIN_SCREEN_PX = 2000;

const ThemeImageFields = observer(({ theme, save }: {
	theme: Theme;
	save: (task: Promise<unknown>) => Promise<void>;
}) => {
	const image = theme.image;
	const set = (style: ThemeImageStyle) => save(themeStore.setThemeImage(theme.id, style));

	return (
		<View gap="md">
			<ThemeImageSourceField theme={theme} save={save} />

			<ImposedField
				field="scale"
				label="Taille"
				imposed={image.scale !== undefined}
				onImpose={() => set({ scale: 1 })}
				onRelease={() => set({ scale: undefined })}
			>
				<ThemeShareSlider value={image.scale ?? 1} onSave={scale => set({ scale: Math.max(scale, 0.01) })} />
			</ImposedField>

			<ImposedField
				field="radius"
				label="Arrondi des coins"
				imposed={image.radius !== undefined}
				onImpose={() => set({ radius: 0 })}
				onRelease={() => set({ radius: undefined })}
			>
				<ThemeShareSlider value={image.radius ?? 0} factor={200} onSave={radius => set({ radius })} />
			</ImposedField>

			<ImposedField
				field="opacity"
				label="Opacité"
				imposed={image.opacity !== undefined}
				onImpose={() => set({ opacity: 1 })}
				onRelease={() => set({ opacity: undefined })}
			>
				<ThemeShareSlider value={image.opacity ?? 1} onSave={opacity => set({ opacity })} />
			</ImposedField>

			<ThemePositionField value={image} onChange={set} />

			<ThemeOrientationField value={image} onChange={set} />

			<ImposedField
				field="minScreenPx"
				label="Taille minimale d'affichage"
				imposed={image.minScreenPx !== undefined}
				onImpose={() => set({ minScreenPx: MIN_IMAGE_SCREEN_PX })}
				onRelease={() => set({ minScreenPx: undefined })}
			>
				<ThemeSlider
					value={image.minScreenPx ?? MIN_IMAGE_SCREEN_PX}
					min={0}
					max={SLIDER_MAX_MIN_SCREEN_PX}
					inputMax={MAX_MIN_SCREEN_PX}
					step={5}
					suffix="px"
					onSave={minScreenPx => set({ minScreenPx })}
				/>
			</ImposedField>
		</View>
	);
});

/** L'image imposée aux formes du thème : dans la forme, ou dans son marqueur. */
export const ThemeImageSourceField = observer(({ theme, save, label = 'Image' }: {
	theme: Theme;
	save: (task: Promise<unknown>) => Promise<void>;
	label?: string;
}) => {
	const image = theme.image;
	const set = (style: ThemeImageStyle) => save(themeStore.setThemeImage(theme.id, style));
	const [choosing, setChoosing] = React.useState(false);

	return (
		<ImposedField
			field="src"
			label={label}
			imposed={image.src !== undefined || choosing}
			onImpose={() => setChoosing(true)}
			onRelease={() => {
				setChoosing(false);
				set({ src: undefined });
			}}
		>
			<View row gap="xs">
				<ImageThumbnail src={image.src} onPick={() => pickMapImage(image.src ? `Remplacer l'image` : 'Ajouter une image', src => set({ src }))} />

				<View flex>
					<InputOk
						value={image.src}
						debounce={600}
						onSubmit={async input => {
							const src = (input || '').trim();

							validateImageUrl(src);

							if (src) await set({ src });
						}}
					/>
				</View>
			</View>
		</ImposedField>
	);
});

export default ThemeImageFields;
